import { PublicLayout } from '@/layouts/PublicLayout';
import { Button } from '@/components/Button';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Shield,
  CheckCircle2,
  ArrowRight,
  Eye,
  Scale,
  Clock,
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Eye className="h-6 w-6" />,
      title: 'Glass Box AI',
      description: 'See exactly how AI makes decisions. Every recommendation comes with clear explanations.',
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Bias Detection',
      description: 'Automated bias auditing ensures fair hiring practices that comply with regulations.',
    },
    {
      icon: <Scale className="h-6 w-6" />,
      title: 'Compliant by Design',
      description: 'Built for GDPR, EU AI Act, and NYC Local Law 144 compliance from day one.',
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Save Time',
      description: 'Automate resume screening and candidate matching while maintaining human oversight.',
    },
  ];

  const stats = [
    { label: 'Time Saved', value: '70%' },
    { label: 'Better Matches', value: '3x' },
    { label: 'Compliance Rate', value: '100%' },
    { label: 'User Satisfaction', value: '4.9/5' },
  ];

  const personas = [
    {
      title: 'Job Seekers',
      description: 'Understand why you\'re a match and get fair consideration.',
      benefits: [
        'See AI scoring explanations',
        'Track application status in real-time',
        'Get matched with relevant opportunities',
        'One-click application submissions',
      ],
    },
    {
      title: 'Recruiters',
      description: 'Find the best candidates faster with AI-powered insights.',
      benefits: [
        'Automated resume screening',
        'Bias-free candidate ranking',
        'Instant candidate matching',
        'Collaborative hiring workflows',
      ],
    },
    {
      title: 'Hiring Managers',
      description: 'Make confident hiring decisions backed by data.',
      benefits: [
        'Comprehensive candidate profiles',
        'Interview scheduling automation',
        'Team collaboration tools',
        'Performance analytics',
      ],
    },
    {
      title: 'HR Administrators',
      description: 'Ensure compliance and optimize hiring processes.',
      benefits: [
        'Compliance dashboards',
        'Bias audit reports',
        'System-wide analytics',
        'Policy management',
      ],
    },
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50 pt-20 pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <h1>
                <span className="block text-sm font-semibold uppercase tracking-wide text-primary-600 mb-4">
                  Transparent AI Recruitment
                </span>
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                  Fair Hiring Through{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                    Glass Box AI
                  </span>
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-3xl">
                The only recruitment platform where you can see exactly how AI makes decisions.
                Compliant, transparent, and designed for fairness. Transform your hiring process
                while maintaining human oversight.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:justify-center lg:justify-start">
                <Button size="lg" onClick={() => navigate('/signup')}>
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/features')}>
                  Explore Features
                </Button>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                ✓ Free 14-day trial · ✓ No credit card required · ✓ Cancel anytime
              </p>
            </div>
            <div className="mt-16 sm:mt-24 lg:mt-0 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 rounded-lg bg-primary-100 p-3">
                      <Sparkles className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">AI Screening</h3>
                      <p className="text-gray-600">
                        Candidate match score: 92%
                      </p>
                      <div className="mt-2 text-sm text-gray-500">
                        <p>✓ Skills match: 95%</p>
                        <p>✓ Experience: 8 years (required: 5+)</p>
                        <p>✓ Education: Meets requirements</p>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-500 mb-2">AI Explanation:</p>
                    <p className="text-sm text-gray-700">
                      "Strong match based on technical skills, relevant experience in similar roles,
                      and education qualifications. Bias checks passed ✓"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-bold text-primary-600">{stat.value}</div>
                <div className="mt-2 text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose ePeople Agent?
            </h2>
            <p className="text-lg text-gray-600">
              The first recruitment platform built for transparency, fairness, and compliance.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="rounded-lg bg-primary-100 w-12 h-12 flex items-center justify-center text-primary-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for Everyone in the Hiring Process
            </h2>
            <p className="text-lg text-gray-600">
              Tailored experiences for each role in your organization.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {personas.map((persona) => (
              <div
                key={persona.title}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{persona.title}</h3>
                <p className="text-gray-600 mb-4">{persona.description}</p>
                <ul className="space-y-2">
                  {persona.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start text-sm text-gray-600">
                      <CheckCircle2 className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-secondary-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Compliant by Design
              </h2>
              <p className="text-xl text-primary-100 mb-8">
                Built to meet the strictest global regulations for AI in hiring.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: 'GDPR Compliant',
                    description: 'Full data protection with right to erasure and consent management.',
                  },
                  {
                    title: 'EU AI Act Ready',
                    description: 'Human oversight, transparency, and record-keeping built-in.',
                  },
                  {
                    title: 'NYC Local Law 144',
                    description: 'Automated bias auditing and candidate notifications.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start space-x-4">
                    <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-lg font-semibold text-white">{item.title}</h4>
                      <p className="text-primary-100">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-6">Transparency Features</h3>
                <ul className="space-y-4">
                  {[
                    'Real-time bias detection',
                    'Explainable AI decisions',
                    'Audit trails for all actions',
                    'Candidate notification system',
                    'Human-in-the-loop reviews',
                    'Compliance reporting dashboard',
                  ].map((feature) => (
                    <li key={feature} className="flex items-center text-white">
                      <div className="h-2 w-2 rounded-full bg-white mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Join companies that are making hiring fair, transparent, and compliant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/signup')}>
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/contact')}>
              Schedule Demo
            </Button>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            Questions? <a href="/contact" className="text-primary-600 hover:text-primary-700 font-medium">Contact our team</a>
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
